import apiClient from '../client';
import type { Moment } from '@/types';

export interface MomentEditPayload {
  title?: string | null;
  content?: string | null;
  redirect_url?: string | null;
  is_active?: 0 | 1;
}

export const momentsApi = {
  /**
   * Paginated list of Moments for the current branch.
   * Pass { show_expired: true } to see expired/deactivated rows (audit mode).
   */
  list: (params?: Record<string, unknown>) =>
    apiClient
      .get<{ data: Moment[]; pagination: { total: number; page: number; limit: number } }>(
        '/admin/moments',
        { params },
      )
      .then((r) => r.data),

  getById: (id: number) =>
    apiClient.get<{ data: Moment }>(`/admin/moments/${id}`).then((r) => r.data.data),

  /**
   * Create a Moment.
   *
   * For image/video moments: pass a FormData containing the "media" file.
   * For text moments: pass a plain object with moment_type='text' and content.
   *
   * The function automatically sets the correct Content-Type header.
   */
  create: (payload: FormData | Record<string, unknown>) => {
    const isFormData = payload instanceof FormData;
    return apiClient
      .post<{ data: Moment }>('/admin/moments', payload, {
        // Let the browser set multipart boundary for FormData; use JSON for text moments
        headers: { 'Content-Type': isFormData ? undefined : 'application/json' },
      })
      .then((r) => r.data.data);
  },

  /**
   * Full edit of a Moment's mutable metadata.
   * PUT /admin/moments/:id
   * Media (Drive file) is immutable after creation.
   */
  update: (id: number, payload: MomentEditPayload) =>
    apiClient
      .put<{ data: Moment }>(`/admin/moments/${id}`, payload)
      .then((r) => r.data.data),

  /** Toggle is_active: pass 0 to deactivate, 1 to reactivate. */
  setActive: (id: number, is_active: 0 | 1) =>
    apiClient
      .patch<{ data: Moment }>(`/admin/moments/${id}`, { is_active })
      .then((r) => r.data.data),

  /** Soft-delete a Moment permanently. */
  remove: (id: number) => apiClient.delete(`/admin/moments/${id}`),
};

